import type { WavetablePreset } from "../types";
import { createDefaultWavetablePreset } from "../wavetable/preset";

function base(name: string, patch: Partial<WavetablePreset> = {}): WavetablePreset {
  const p = createDefaultWavetablePreset(name);
  return {
    ...p,
    ...patch,
    osc1: { ...p.osc1, ...patch.osc1 },
    osc2: { ...p.osc2, ...patch.osc2 },
    sub: { ...p.sub, ...patch.sub },
    noise: { ...p.noise, ...patch.noise },
    filter: { ...p.filter, ...patch.filter },
    ampEnv: { ...p.ampEnv, ...patch.ampEnv },
    modEnv: { ...p.modEnv, ...patch.modEnv },
    lfo: { ...p.lfo, ...patch.lfo },
    routes: patch.routes ?? p.routes,
  };
}

/** Factory wavetable patches — table ids from basic / KRC / BVKER catalogs. */
export const WAVETABLE_PRESETS: WavetablePreset[] = [
  base("Wide Supersaw", {
    voices: 7,
    unisonDetune: 18,
    unisonSpread: 0.85,
    unisonEdgeDecay: 0.2,
    osc1: {
      tableId: "basic/saw-square",
      position: 0.15,
      warp: 0,
      warpMode: "none",
      octave: 0,
      semis: 0,
      detuneCents: -4,
      level: 0.8,
    },
    osc2: {
      tableId: "basic/saw-square",
      position: 0.35,
      warp: 0,
      warpMode: "none",
      octave: 0,
      semis: 0,
      detuneCents: 6,
      level: 0.65,
    },
    filter: { type: "lowpass", cutoffHz: 5200, resonance: 0.2 },
    routes: [
      { source: "modEnv", target: "cutoff", depth: 0.35 },
      { source: "lfo", target: "tablePos1", depth: 0.08 },
    ],
  }),
  base("Analog PWM Pad", {
    voices: 5,
    unisonDetune: 10,
    unisonSpread: 0.7,
    osc1: {
      tableId: "bvker/analog/analog-pwm-saw-01",
      position: 0.3,
      warp: 0.1,
      warpMode: "bend",
      octave: 0,
      semis: 0,
      detuneCents: 0,
      level: 0.75,
    },
    osc2: {
      tableId: "bvker/analog/analog-pwm-square-01",
      position: 0.55,
      warp: 0,
      warpMode: "none",
      octave: 0,
      semis: 7,
      detuneCents: 3,
      level: 0.4,
    },
    sub: { wave: "sine", octave: -1, level: 0.25 },
    ampEnv: { a: 0.08, d: 0.4, s: 0.75, r: 0.6 },
  }),
  base("Growl Bass", {
    voices: 3,
    unisonDetune: 8,
    unisonSpread: 0.35,
    unisonEdgeDecay: 0.1,
    osc1: {
      tableId: "bvker/growl/growl-01",
      position: 0.4,
      warp: 0.25,
      warpMode: "sync",
      octave: -1,
      semis: 0,
      detuneCents: 0,
      level: 0.9,
    },
    osc2: {
      tableId: "basic/mirror-fold",
      position: 0.6,
      warp: 0.4,
      warpMode: "mirror",
      octave: -1,
      semis: 0,
      detuneCents: 5,
      level: 0.45,
    },
    sync: true,
    pmDepth: 0.35,
    filter: { type: "lowpass", cutoffHz: 1800, resonance: 0.45 },
    ampEnv: { a: 0.005, d: 0.25, s: 0.85, r: 0.2 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 0.55 }],
  }),
  base("Glass Keys", {
    voices: 1,
    unisonDetune: 0,
    unisonSpread: 0,
    osc1: {
      tableId: "basic/sine-saw",
      position: 0.1,
      warp: 0,
      warpMode: "none",
      octave: 1,
      semis: 0,
      detuneCents: 0,
      level: 0.7,
    },
    osc2: {
      tableId: "basic/harmonics",
      position: 0.45,
      warp: 0,
      warpMode: "none",
      octave: 2,
      semis: 0,
      detuneCents: 2,
      level: 0.35,
    },
    filter: { type: "lowpass", cutoffHz: 8000, resonance: 0.15 },
    ampEnv: { a: 0.002, d: 0.55, s: 0.15, r: 0.45 },
    routes: [{ source: "modEnv", target: "tablePos2", depth: 0.4 }],
  }),
  base("FM Metallic", {
    voices: 4,
    unisonDetune: 6,
    unisonSpread: 0.5,
    osc1: {
      tableId: "bvker/fm/fm-01",
      position: 0.25,
      warp: 0,
      warpMode: "none",
      octave: 0,
      semis: 0,
      detuneCents: 0,
      level: 0.8,
    },
    osc2: {
      tableId: "basic/metallic",
      position: 0.7,
      warp: 0.2,
      warpMode: "bend",
      octave: 0,
      semis: 0,
      detuneCents: -3,
      level: 0.5,
    },
    pmDepth: 0.6,
    filter: { type: "bandpass", cutoffHz: 2400, resonance: 0.35 },
  }),
  base("Hyper Lead", {
    voices: 5,
    unisonDetune: 14,
    unisonSpread: 0.55,
    osc1: {
      tableId: "bvker/hyper/hyper-02",
      position: 0.35,
      warp: 0.15,
      warpMode: "sync",
      octave: 0,
      semis: 0,
      detuneCents: 0,
      level: 0.85,
    },
    osc2: {
      tableId: "basic/tri-pulse",
      position: 0.5,
      warp: 0,
      warpMode: "none",
      octave: 1,
      semis: 0,
      detuneCents: 7,
      level: 0.3,
    },
    filter: { type: "lowpass", cutoffHz: 6500, resonance: 0.3 },
    ampEnv: { a: 0.01, d: 0.15, s: 0.7, r: 0.25 },
    routes: [
      { source: "lfo", target: "unisonDetune", depth: 0.2 },
      { source: "modEnv", target: "cutoff", depth: 0.4 },
    ],
  }),
  base("Soft Ambient", {
    voices: 9,
    unisonDetune: 22,
    unisonSpread: 1,
    unisonEdgeDecay: 0.45,
    osc1: {
      tableId: "krc/raim-00001-free",
      position: 0.2,
      warp: 0,
      warpMode: "none",
      octave: 0,
      semis: 0,
      detuneCents: 0,
      level: 0.6,
    },
    osc2: {
      tableId: "basic/soft-bright",
      position: 0.4,
      warp: 0,
      warpMode: "none",
      octave: 0,
      semis: 5,
      detuneCents: 4,
      level: 0.45,
    },
    filter: { type: "lowpass", cutoffHz: 2800, resonance: 0.1 },
    ampEnv: { a: 0.4, d: 0.8, s: 0.85, r: 1.2 },
    lfo: { rateHz: 0.12, wave: "sine", depth: 0.25 },
    routes: [
      { source: "lfo", target: "tablePos1", depth: 0.2 },
      { source: "lfo", target: "unisonSpread", depth: 0.15 },
    ],
  }),
  base("Distorted Stab", {
    voices: 2,
    unisonDetune: 4,
    unisonSpread: 0.4,
    osc1: {
      tableId: "bvker/distorted/dist-hard-clipping",
      position: 0.5,
      warp: 0.3,
      warpMode: "mirror",
      octave: 0,
      semis: 0,
      detuneCents: 0,
      level: 0.85,
    },
    osc2: {
      tableId: "basic/pwm",
      position: 0.7,
      warp: 0,
      warpMode: "none",
      octave: 0,
      semis: 0,
      detuneCents: 8,
      level: 0.4,
    },
    noise: { level: 0.08 },
    filter: { type: "highpass", cutoffHz: 180, resonance: 0.2 },
    ampEnv: { a: 0.001, d: 0.12, s: 0.2, r: 0.15 },
  }),
  base("Mono PWM Bass", {
    voices: 1,
    osc1: {
      tableId: "basic/pwm",
      position: 0.4,
      warp: 0,
      warpMode: "none",
      octave: -1,
      semis: 0,
      detuneCents: 0,
      level: 0.9,
    },
    osc2: {
      tableId: "basic/saw-square",
      position: 0.1,
      warp: 0,
      warpMode: "none",
      octave: -1,
      semis: 0,
      detuneCents: 0,
      level: 0.25,
    },
    sub: { wave: "square", octave: -2, level: 0.35 },
    filter: { type: "lowpass", cutoffHz: 1200, resonance: 0.4 },
    routes: [{ source: "modEnv", target: "cutoff", depth: 0.6 }],
  }),
  base("KRC Explore Pad", {
    voices: 6,
    unisonDetune: 15,
    unisonSpread: 0.75,
    osc1: {
      tableId: "krc/krc-rand-explore-1-free",
      position: 0.3,
      warp: 0,
      warpMode: "none",
      octave: 0,
      semis: 0,
      detuneCents: 0,
      level: 0.7,
    },
    osc2: {
      tableId: "krc/vol-1-mband-1-free",
      position: 0.55,
      warp: 0.1,
      warpMode: "bend",
      octave: 0,
      semis: 0,
      detuneCents: 5,
      level: 0.5,
    },
    ampEnv: { a: 0.15, d: 0.5, s: 0.8, r: 0.7 },
  }),
];
